// Replacement Quote (RQ / "order") API calls — same endpoints and params as the Android technician app
// (api/v3/pro/orders/*). Every call resolves to { ok, body, message, activeRecord }, never throws, and shows the loader.
import { apiCall } from "./apiCall";

// every call shows the global loader (apiCall)
const call = (method, url, data) => apiCall(method, url, data);
// multipart body; empty values are left out
const form = (fields) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") fd.append(k, v); });
  return fd;
};

export const rqApi = {
  // list / detail
  list: (params) => call("get", "orders/list", params),
  detail: (orderId) => call("get", "orders/get", { order_id: orderId }),
  quickSummary: (orderId) => call("get", "orders/summary/quick", { order_id: orderId }),
  // the System Summary page as JSON (G-80; the panel's SummaryController shape + can_edit_notes)
  summaryShow: (orderId) => call("get", "orders/summary/show", { order_id: orderId }),
  // the summary notes box (one note per quote; only the quote's sales person gets can_edit_notes)
  summaryNotes: (orderId, notes) => call("post", "orders/summary/notes", { order_id: orderId, notes }),
  // Edit Summary form (G-78): load + save the summary / line titles; save rebuilds the stored summary
  summaryEditData: (orderId) => call("get", "orders/summary/edit-data", { order_id: orderId }),
  summaryUpdate: (orderId, fields) => call("post", "orders/summary/update", { order_id: orderId, ...fields }),

  // create (server-hosted web flow) and the guard pre-check
  allowCreate: (appointmentId) => call("post", "orders/allow/create", appointmentId ? { appointment_id: appointmentId } : {}),
  singleActiveRecord: (orderId) => call("get", "single-active-record", orderId ? { order_id: orderId } : {}),

  // status flow
  accept: (orderId) => call("post", "orders/accept", { order_id: orderId }),
  reject: (orderId) => call("post", "orders/reject", { order_id: orderId }),
  enRoute: (orderId) => call("post", "orders/en-route", { order_id: orderId }),
  arrived: (orderId) => call("post", "orders/arrived", { order_id: orderId }),
  inspectionStart: (orderId) => call("post", "orders/inspection/start", { order_id: orderId }),
  inspectionSubmit: (orderId, items, type, subType) => call("post", "orders/inspection/submit", {
    order_id: orderId, items: JSON.stringify(items || []), ...(type ? { type } : {}), ...(subType ? { sub_type: subType } : {}),
  }),
  installationStart: (orderId) => call("post", "orders/installation/start", { order_id: orderId }),
  installationPause: (orderId) => call("post", "orders/installation/pause", { order_id: orderId }),
  installationComplete: (payload) => call("post", "orders/installation/complete", payload),
  installationItems: (params) => call("get", "orders/installation/items", params),
  installationDatetime: (orderId, unixSeconds) => call("post", "orders/installation/datetime", { order_id: orderId, installation_datetime: unixSeconds }),
  inspectionReady: (orderId) => call("post", "inspection/ready", { order_id: orderId }),
  inspectionNotReady: (orderId, notes) => call("post", "inspection/not-ready", { order_id: orderId, notes }),
  disposeReasons: (orderId) => call("get", "dispose-reasons", { order_id: orderId }),
  dispose: (orderId, reasonId, notes) => call("post", "orders/dispose", { order_id: orderId, reason_id: reasonId, dispose_notes: notes || "" }),
  resume: (orderId, notes) => call("post", "orders/resume", { order_id: orderId, resume_notes: notes }),

  // additional items + payment type (Submit PerfectMatch)
  items: (orderId) => call("get", "orders/items/list", { order_id: orderId }),
  saveItems: (orderId, items) => call("post", "orders/items/submit", { order_id: orderId, items: JSON.stringify(items) }),
  paymentTypes: (orderId) => call("get", "orders/payment-type/amount", { order_id: orderId }),

  // notes, documents, invoices
  notes: (orderId) => call("get", "orders/notes/list", { order_id: orderId }),
  addNote: (orderId, notes) => call("post", "orders/notes/store", { order_id: orderId, notes }),
  documents: (orderId) => call("get", "orders/document/list", { order_id: orderId }),
  // required document slot (multipart: order_id, document_id, document); returns the whole documents list
  uploadDocument: (orderId, documentId, file) => call("post", "orders/document/upload", form({ order_id: orderId, document_id: documentId, document: file })),
  // other document (multipart: order_id, title, document; id = replace that one)
  uploadOtherDocument: (orderId, title, file, id) => call("post", "orders/document/other/upload", form({ order_id: orderId, title, document: file, id })),
  // axios.delete(url, config): send the ids as query params
  removeOtherDocument: (orderId, id) => call("delete", "orders/document/other/remove", { params: { order_id: orderId, id } }),
  invoices: (orderId) => call("get", "orders/invoices", { order_id: orderId }),
  createInvoice: (orderId) => call("post", "invoice/create", { job_id: orderId, job_type: "rq" }),

  // proposal / checkout link / QR / vendor order / data sheet
  sendProposal: (orderId, email) => call("post", "orders/send-proposal", { order_id: orderId, email }),
  resendCheckoutLink: (orderId, email, phone) => call("get", `orders/resend-checkout-url/${orderId}`, { email, phone }),
  qrCode: (orderId) => call("get", "qrCode/job-detail", { job_id: orderId, job_source: "rq" }),
  vendorOrder: (orderId) => call("get", `orders/vendor-order/show/${orderId}`),
  placeVendorOrder: (rqId) => call("post", `orders/vendor-order/store/${rqId}`, {}),
  // the server puts `_token` into the sheet url it returns (a frame has no Authorization header)
  dataSheet: (orderId) => call("get", "orders/sheet/show", { order_id: orderId, time_offset: -new Date().getTimezoneOffset(), _token: JSON.parse(localStorage.getItem("user"))?._token }),
  members: (consumerId) => call("get", "consumer/member/list", { consumer_id: consumerId }),
  // data sheet wizard: draft on every step (data = JSON of the fields so far), final submit = flat form fields
  saveDataSheetDraft: (orderId, data) => call("post", "orders/sheet/partial/create", { order_id: orderId, data: JSON.stringify(data) }),
  submitDataSheet: (orderId, fields) => call("post", "orders/sheet/create", { order_id: orderId, ...fields }),

  // add discount (status 5)
  discounts: (orderId) => call("get", "discount-code/list", { order_id: orderId }),
  applyDiscount: (orderId, discount, promoCode) => call("post", "discount-code/apply2", { order_id: orderId, discount, promo_code: promoCode }),
  cancelDiscount: (orderId, promoCodeId) => call("put", "discount-code/cancel", { order_id: orderId, promo_code_id: promoCodeId }),

  // system scanner (serial / model numbers per system)
  barcodeManufacturers: () => call("get", "orders/barcodes/manufacturers"),
  barcodes: (orderId) => call("get", "orders/barcodes/list", { order_id: orderId }),
  saveBarcodes: (orderId, barcode) => call("post", "orders/barcodes/store", { order_id: orderId, barcode }),

  // payment acknowledgement (order.acknowledgement required): cash / check / atwood / other
  paymentData: (orderId) => call("get", "payment/data", { id: orderId, model: "order" }),
  paymentDecision: (orderId, decision, event) => call("post", "payment/decision", { id: orderId, model: "order", decision, event }),
  paymentAtwood: (orderId, revisedAmount, event) => call("post", "payment/atwood", { id: orderId, model: "order", revised_amount: revisedAmount, event }),

  // other jobs on the requested day (new order popup)
  bookingOrders: (orderId) => call("get", "booking-order", { order_id: orderId }),
};

// Order statuses (Laravel app/Models/Interfaces/Order/Definition.php)
export const RQ_STATUS = {
  PENDING: 0, ACCEPTED: 1, EN_ROUTE: 2, ARRIVED: 3, INSPECTION_STARTED: 4, SUBMITTED: 5,
  INSTALLATION_PENDING: 6, INSTALLATION_STARTED: 7, INSTALLATION_PAUSE: 8, INSTALLATION_COMPLETED: 9,
  REQUEST_CANCEL: 10, CANCEL: 11, REJECTED: 12, FINAL_INSPECTION: 13, RE_OPEN: 14, READY_FOR_INSPECTION: 15,
  CLOSED: 16, VOID_REFUND: 17, DISPOSED: 18,
};

// Badge colours by status text — shared by the RQ list and detail
export function rqStatusColor(text) {
  const t = (text || "").toLowerCase();
  if (t.includes("closed") || t.includes("void")) return "#5B616B";
  if (t.includes("cancel") || t.includes("reject") || t.includes("disposed")) return "#D7385E";
  if (t.includes("installation completed")) return "#1FA971";
  if (t.includes("installation")) return "#429538";
  if (t.includes("inspection submitted")) return "#9B7EA4";
  if (t.includes("inspection started") || t.includes("reopen")) return "#F67280";
  if (t.includes("permit") || t.includes("final")) return "#0F766E";
  if (t.includes("arrived")) return "#14B8A6";
  if (t.includes("en route")) return "#00B7C2";
  if (t.includes("accepted")) return "#7695FF";
  if (t.includes("pending")) return "#F0A92B";
  return "#6B7280";
}

// Status line under the badge (Android OrderLandingFragment status descriptions)
export function rqStatusDescription(status, firstName) {
  const name = firstName || "the consumer";
  return {
    1: "Good job! Make sure to respond on time.",
    2: "All the best! Let's fyxify.",
    3: "Time to inspect the location.",
    4: `Let's give ${name} the best system for the home.`,
    5: `Wait for ${name} to approve the inspection report.`,
    6: `${name} has approved the quote. Start installing.`,
    7: "Installation work in progress...",
    8: "Installation is paused. Hit \"Resume Installation\" to continue.",
    9: "This job has been completed.",
  }[Number(status)] || "";
}
